import { FC, PropsWithChildren, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
	Dialog,
	DialogContent,
	DialogTitle,
	DialogDescription,
	DialogHeader,
	DialogFooter,
	DialogClose,
} from '@/components/ui/dialog';
import ETextInput from '@/components/everridge/ETextInput';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import ETextAreaInput from '@/components/everridge/ETextAreaInput';
import { NewAndOfferManageFormSchema } from '@/features/news-and-offers/validators/NewAndOfferManageValidator';
import ETagPicker from '@/components/everridge/ETagPicker';
import {
	CreateNewAndOfferRequestDto,
	NewsAndOffersStatus,
	NewsAndOffersType,
	UpdateNewAndOfferRequestDto,
} from '@/features/news-and-offers/types/news-and-offers-dto';
import EImageUploaderForm from '@/components/everridge/EImageUploaderForm';
import EDatePicker from '@/components/everridge/EDatePicker';
import { useNewAndOfferDetailsApi } from '@/features/news-and-offers/hooks/useNewAndOfferDetailsApi';
import Cancel_01_Icon from '@/components/icons/Cancel_01_Icon';
import useMediaQuery from '@/hooks/useMediaQuery';
import { AppConfig } from '@/config/app.config';
import { cn } from '@/lib/utils';

type FormData = z.infer<typeof NewAndOfferManageFormSchema>;

type Props = {
	id?: string;
	onClose: () => void;
};

const NewAndOfferManage: FC<PropsWithChildren<Props>> = ({ id, onClose }) => {
	const [isOpen, setIsOpen] = useState(true);

	const isMobile = useMediaQuery(AppConfig.mediaQueries.mobile);
	const isSubmittingRef = useRef(false);
	const { t } = useTranslation();

	const {
		newAndOfferDetailsData,
		createNewAndOffer,
		isCreatingNewAndOffer,
		updateNewAndOffer,
		isUpdatingingNewAndOffer,
	} = useNewAndOfferDetailsApi({
		id,
	});

	const { control, handleSubmit, reset } = useForm<FormData>({
		resolver: zodResolver(NewAndOfferManageFormSchema),
	});

	useEffect(() => {
		if (newAndOfferDetailsData) {
			reset({
				startDate: newAndOfferDetailsData.startDate,
				endDate: newAndOfferDetailsData.endDate,
				title: newAndOfferDetailsData.title ?? '',
				description: newAndOfferDetailsData.description ?? '',
				type: newAndOfferDetailsData.type,
				status: newAndOfferDetailsData.status,
				imageUrl: newAndOfferDetailsData.imageUrl,
			});
		}
	}, [newAndOfferDetailsData, reset]);

	const handleClose = () => {
		setIsOpen(false);
		setTimeout(onClose, 300);
	};

	const handleCreateForm = async (
		form: FormData,
		status: NewsAndOffersStatus,
	) => {
		if (isSubmittingRef.current) return;
		isSubmittingRef.current = true;

		const { imageUrl: imageData } = form;

		const dto: CreateNewAndOfferRequestDto = {
			startDate: form.startDate,
			endDate: form.endDate,
			title: form.title,
			description: form.description,
			type: form.type,
			status: status,
		};

		await createNewAndOffer({ dto, imageData });
		isSubmittingRef.current = false;
		handleClose();
	};

	const handleUpdateForm = async (form: FormData) => {
		if (isSubmittingRef.current) return;
		isSubmittingRef.current = true;

		const { imageUrl: imageData } = form;

		const dto: UpdateNewAndOfferRequestDto = {
			startDate: form.startDate,
			endDate: form.endDate,
			title: form.title,
			description: form.description,
			type: form.type,
			status: form.status as NewsAndOffersStatus,
		};

		await updateNewAndOffer({ dto, imageData });
		isSubmittingRef.current = false;
		onClose();
	};

	const newAndOfferTypes = Object.values(NewsAndOffersType).filter(
		(value) => typeof value === 'string',
	);

	const disabled =
		isSubmittingRef.current ||
		isCreatingNewAndOffer ||
		isUpdatingingNewAndOffer;

	const postButtons = (
		<>
			<Button
				variant='outline'
				onClick={handleSubmit((form) =>
					handleCreateForm(form, NewsAndOffersStatus.Draft),
				)}
				disabled={disabled}>
				{t('elements.buttons.saveDraft')}
			</Button>
			<Button
				onClick={handleSubmit((form) =>
					handleCreateForm(form, NewsAndOffersStatus.Active),
				)}
				disabled={disabled}>
				{!!id
					? t('elements.buttons.postDraft')
					: t('elements.buttons.post')}
			</Button>
		</>
	);

	const editButtons = (
		<>
			<Button
				variant='outline'
				onClick={onClose}>
				{t('elements.buttons.cancel')}
			</Button>
			<Button
				onClick={handleSubmit(handleUpdateForm)}
				disabled={disabled}>
				{t('elements.buttons.saveChanges')}
			</Button>
		</>
	);

	const today = new Date();
	const tomorrow = new Date(today);
	tomorrow.setDate(today.getDate() + 1);

	return (
		<Dialog
			open={isOpen}
			onOpenChange={handleClose}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>
						{!!id
							? t(
									'main.newsAndOffers.components.manage.editTitle',
							  )
							: t(
									'main.newsAndOffers.components.manage.createTitle',
							  )}
						<DialogClose
							className={cn(
								'ml-auto flex items-center justify-center cursor-pointer',
								isMobile &&
									'size-11 border border-popover-100 rounded-full',
							)}>
							<Cancel_01_Icon />
						</DialogClose>
					</DialogTitle>
					<DialogDescription />
				</DialogHeader>
				<div className='flex flex-col gap-4 overflow-y-auto'>
					<EImageUploaderForm
						control={control}
						fieldName={'imageUrl'}
						label={t('elements.form.imageUploader.label')}
					/>
					<ETagPicker
						control={control}
						fieldName={'type'}
						label={t('elements.form.tagPicker.label')}
						tags={newAndOfferTypes}
					/>
					<ETextInput
						control={control}
						fieldName={'title'}
						label={t('elements.form.title.label')}
						placeholder={t('elements.form.title.placeholder')}
					/>
					<ETextAreaInput
						control={control}
						fieldName={'description'}
						label={t('elements.form.description.label')}
						placeholder={t('elements.form.description.placeholder')}
					/>
					<div className='flex gap-4'>
						<EDatePicker
							control={control}
							fieldName={'startDate'}
							label={t('elements.form.startDate.label')}
							placeholder={t(
								'elements.form.startDate.placeholder',
							)}
							fromDate={today}
						/>
						<EDatePicker
							control={control}
							fieldName={'endDate'}
							label={t('elements.form.endDate.label')}
							placeholder={t('elements.form.endDate.placeholder')}
							fromDate={tomorrow}
						/>
					</div>
				</div>
				<DialogFooter
					className={cn('justify-end', isMobile && 'mt-auto')}>
					{!!id ? editButtons : postButtons}
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};

export default NewAndOfferManage;
